import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FiSearch, FiPlus, FiEdit2, FiTrash2, FiX } from 'react-icons/fi';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const TaxManagement = ({ propertyId }) => {
  // ==================== TAB STATE ====================
  const [activeTab, setActiveTab] = useState('tax');

  // ==================== SERVICE FEE STATE ====================
  const [serviceFees, setServiceFees] = useState([]);
  const [filteredServiceFees, setFilteredServiceFees] = useState([]);
  const [feeLoading, setFeeLoading] = useState(false);
  const [feeError, setFeeError] = useState(null);
  const [isEditingFee, setIsEditingFee] = useState(false);
  const [currentFee, setCurrentFee] = useState(null);
  const [feeSearchTerm, setFeeSearchTerm] = useState('');
  const [showFeeModal, setShowFeeModal] = useState(false);
  const [feeFormData, setFeeFormData] = useState({
    fee_name: '',
    fee_amount: '',
    fee_type: '',
    start_date: '',
    end_date: '',
  });

  // ==================== TAX STATE ====================
  const [taxes, setTaxes] = useState([]);
  const [filteredTaxes, setFilteredTaxes] = useState([]);
  const [taxLoading, setTaxLoading] = useState(false);
  const [taxError, setTaxError] = useState(null);
  const [isEditingTax, setIsEditingTax] = useState(false);
  const [currentTax, setCurrentTax] = useState(null);
  const [taxSearchTerm, setTaxSearchTerm] = useState('');
  const [showTaxModal, setShowTaxModal] = useState(false);
  const [taxFormData, setTaxFormData] = useState({
    taxname: '',
    value: '',
    priority: '',
    definition: '',
    value_type: '',
  });

  // ==================== SERVICE FEE FUNCTIONS ====================

  const fetchServiceFees = async () => {
    try {
      setFeeLoading(true);
      setFeeError(null);

      const response = await fetch(`${BQ_BASE_URL}/bq/api/tax/service-fee/?skip=0&limit=100`, {
        headers: {
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Service fee response:', data); // Debug log

      // Check if data has service_fees property
      if (data && data.service_fees) {
        setServiceFees(data.service_fees);
        setFilteredServiceFees(data.service_fees);
      } else {
        // If the response is an array directly
        if (Array.isArray(data)) {
          setServiceFees(data);
          setFilteredServiceFees(data);
        } else {
          setServiceFees([]);
          setFilteredServiceFees([]);
          console.warn('Unexpected response format:', data);
        }
      }
      setFeeLoading(false);
    } catch (err) {
      console.error('Error fetching service fees:', err);
      setFeeError(err.message || 'Failed to fetch service fees');
      setFeeLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'service-fee') {
      fetchServiceFees();
    }
  }, [activeTab]);

  useEffect(() => {
    if (feeSearchTerm === '') {
      setFilteredServiceFees(serviceFees);
    } else {
      const filtered = serviceFees.filter((fee) =>
        fee.fee_name.toLowerCase().includes(feeSearchTerm.toLowerCase())
      );
      setFilteredServiceFees(filtered);
    }
  }, [feeSearchTerm, serviceFees]);

  const handleFeeInputChange = (e) => {
    const { name, value } = e.target;
    setFeeFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFeeSearchChange = (e) => {
    setFeeSearchTerm(e.target.value);
  };

  const handleFeeEditClick = (fee) => {
    setIsEditingFee(true);
    setCurrentFee(fee);
    setShowFeeModal(true);
    setFeeFormData({
      fee_name: fee.fee_name,
      fee_amount: fee.fee_amount,
      fee_type: fee.fee_type,
      start_date: fee.start_date ? fee.start_date.split('T')[0] : '',
      end_date: fee.end_date ? fee.end_date.split('T')[0] : '',
    });
  };

  const handleFeeCancelEdit = () => {
    setIsEditingFee(false);
    setCurrentFee(null);
    setShowFeeModal(false);
    setFeeFormData({
      fee_name: '',
      fee_amount: '',
      fee_type: '',
      start_date: '',
      end_date: '',
    });
  };

  const handleFeeSubmit = async (e) => {
    e.preventDefault();

    try {
      const url = isEditingFee
        ? `${BQ_BASE_URL}/bq/api/tax/service-fee/${currentFee.fee_id}`
        : `${BQ_BASE_URL}/bq/api/tax/service-fee/`;

      const payload = {
        ...feeFormData,
        fee_amount: parseFloat(feeFormData.fee_amount),
        propertyid: propertyId,
      };

      const response = await fetch(url, {
        method: isEditingFee ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to save service fee');
      }

      toast.success(
        data.message || (isEditingFee ? 'Service fee updated!' : 'Service fee created!')
      );
      fetchServiceFees();
      handleFeeCancelEdit();
    } catch (err) {
      toast.error(err.message || 'An error occurred');
    }
  };

  const handleFeeDelete = async (feeId) => {
    if (window.confirm('Are you sure you want to delete this service fee?')) {
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/tax/service-fee/${feeId}`, {
          method: 'DELETE',
          headers: {
            accept: 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to delete service fee');
        }

        const result = await response.json();
        toast.success(result.message);
        fetchServiceFees();
      } catch (err) {
        toast.error(err.message);
      }
    }
  };

  // ==================== TAX FUNCTIONS ====================

  const fetchTaxes = async () => {
    try {
      setTaxLoading(true);
      setTaxError(null);

      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/tax/?propertyid=${propertyId}&skip=0&limit=100`,
        {
          headers: {
            accept: 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Tax response:', data); // Debug log

      if (data && data.taxes) {
        setTaxes(data.taxes);
        setFilteredTaxes(data.taxes);
      } else {
        setTaxes([]);
        setFilteredTaxes([]);
      }
      setTaxLoading(false);
    } catch (err) {
      console.error('Error fetching taxes:', err);
      setTaxError(err.message || 'Failed to fetch taxes');
      setTaxLoading(false);
    }
  };

  useEffect(() => {
    if (propertyId && activeTab === 'tax') {
      fetchTaxes();
    }
  }, [propertyId, activeTab]);

  useEffect(() => {
    if (taxSearchTerm === '') {
      setFilteredTaxes(taxes);
    } else {
      const filtered = taxes.filter((tax) =>
        tax.taxname.toLowerCase().includes(taxSearchTerm.toLowerCase())
      );
      setFilteredTaxes(filtered);
    }
  }, [taxSearchTerm, taxes]);

  const handleTaxInputChange = (e) => {
    const { name, value } = e.target;
    setTaxFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTaxSearchChange = (e) => {
    setTaxSearchTerm(e.target.value);
  };

  const handleTaxEditClick = (tax) => {
    setIsEditingTax(true);
    setCurrentTax(tax);
    setShowTaxModal(true);
    setTaxFormData({
      taxname: tax.taxname,
      value: tax.value,
      priority: tax.priority,
      definition: tax.definition || '',
      value_type: tax.value_type || '',
    });
  };

  const handleTaxCancelEdit = () => {
    setIsEditingTax(false);
    setCurrentTax(null);
    setShowTaxModal(false);
    setTaxFormData({
      taxname: '',
      value: '',
      priority: '',
      definition: '',
      value_type: '',
    });
  };

  const handleTaxSubmit = async (e) => {
    e.preventDefault();

    try {
      const url = isEditingTax
        ? `${BQ_BASE_URL}/bq/api/tax/${currentTax.taxid}`
        : `${BQ_BASE_URL}/bq/api/tax/`;

      const payload = {
        ...taxFormData,
        value: parseFloat(taxFormData.value),
        priority: parseInt(taxFormData.priority),
        propertyid: propertyId,
      };

      const response = await fetch(url, {
        method: isEditingTax ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to save tax');
      }

      toast.success(data.message || (isEditingTax ? 'Tax updated!' : 'Tax created!'));
      fetchTaxes();
      handleTaxCancelEdit();
    } catch (err) {
      toast.error(err.message || 'An error occurred');
    }
  };

  const handleTaxDelete = async (taxId) => {
    if (window.confirm('Are you sure you want to delete this tax?')) {
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/tax/${taxId}`, {
          method: 'DELETE',
          headers: {
            accept: 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to delete tax');
        }

        const result = await response.json();
        toast.success(result.message);
        fetchTaxes();
      } catch (err) {
        toast.error(err.message);
      }
    }
  };

  // ==================== SERVICE FEE MODAL ====================
  const ServiceFeeModal = () => {
    if (!showFeeModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center p-6 border-b border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900">
              {isEditingFee ? 'Edit Service Fee' : 'Add New Service Fee'}
            </h3>
            <button
              onClick={handleFeeCancelEdit}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <FiX size={24} />
            </button>
          </div>

          <form onSubmit={handleFeeSubmit} className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fee Name*</label>
                <input
                  type="text"
                  name="fee_name"
                  value={feeFormData.fee_name}
                  onChange={handleFeeInputChange}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-black focus:border-transparent"
                  placeholder="e.g., Cleaning Fee"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fee Amount*</label>
                <input
                  type="number"
                  name="fee_amount"
                  value={feeFormData.fee_amount}
                  onChange={handleFeeInputChange}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-black focus:border-transparent"
                  placeholder="0.00"
                  required
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fee Type*</label>
                <select
                  name="fee_type"
                  value={feeFormData.fee_type}
                  onChange={handleFeeInputChange}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-black focus:border-transparent"
                  required
                >
                  <option value="">Select type</option>
                  <option value="F">Fixed</option>
                  <option value="P">Percentage</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date*</label>
                <input
                  type="date"
                  name="start_date"
                  value={feeFormData.start_date}
                  onChange={handleFeeInputChange}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-black focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date*</label>
                <input
                  type="date"
                  name="end_date"
                  value={feeFormData.end_date}
                  onChange={handleFeeInputChange}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-black focus:border-transparent"
                  required
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-6 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleFeeCancelEdit}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors"
              >
                {isEditingFee ? 'Update Service Fee' : 'Add Service Fee'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // ==================== TAX MODAL ====================
  const TaxModal = () => {
    if (!showTaxModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center p-6 border-b border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900">
              {isEditingTax ? 'Edit Tax' : 'Add New Tax'}
            </h3>
            <button
              onClick={handleTaxCancelEdit}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <FiX size={24} />
            </button>
          </div>

          <form onSubmit={handleTaxSubmit} className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tax Name*</label>
                <input
                  type="text"
                  name="taxname"
                  value={taxFormData.taxname}
                  onChange={handleTaxInputChange}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-black focus:border-transparent"
                  placeholder="e.g., Sales Tax"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Value*</label>
                <input
                  type="number"
                  name="value"
                  value={taxFormData.value}
                  onChange={handleTaxInputChange}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-black focus:border-transparent"
                  placeholder="0.00"
                  required
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority*</label>
                <input
                  type="number"
                  name="priority"
                  value={taxFormData.priority}
                  onChange={handleTaxInputChange}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-black focus:border-transparent"
                  placeholder="1"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Value Type</label>
                <select
                  name="value_type"
                  value={taxFormData.value_type}
                  onChange={handleTaxInputChange}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-black focus:border-transparent"
                >
                  <option value="">Select type</option>
                  <option value="P">Percentage (%)</option>
                  <option value="S">Fixed Amount</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  name="definition"
                  value={taxFormData.definition}
                  onChange={handleTaxInputChange}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-black focus:border-transparent"
                  placeholder="Enter tax description..."
                  rows="3"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-6 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleTaxCancelEdit}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors"
              >
                {isEditingTax ? 'Update Tax' : 'Add Tax'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // ==================== RENDER FUNCTIONS ====================

  const renderServiceFeeTab = () => {
    if (feeLoading) {
      return <div className="text-center py-4">Loading service fees...</div>;
    }

    if (feeError) {
      return <div className="text-center text-red-500 py-4">{feeError}</div>;
    }

    return (
      <div>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-4">
          <div className="relative w-full md:w-64">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiSearch className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search service fees..."
              value={feeSearchTerm}
              onChange={handleFeeSearchChange}
              className="pl-10 w-full p-2 border border-gray-300 rounded"
            />
          </div>
          <button
            onClick={() => {
              setIsEditingFee(false);
              setCurrentFee(null);
              setShowFeeModal(true);
              setFeeFormData({
                fee_name: '',
                fee_amount: '',
                fee_type: '',
                start_date: '',
                end_date: '',
              });
            }}
            className="flex items-center px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors"
          >
            <FiPlus className="mr-2" />
            Add New Service Fee
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead className="bg-gray-100">
              <tr>
                <th className="py-3 px-4 border-b border-gray-200 text-left text-sm font-semibold text-gray-700">
                  Fee Name
                </th>
                <th className="py-3 px-4 border-b border-gray-200 text-left text-sm font-semibold text-gray-700">
                  Amount
                </th>
                <th className="py-3 px-4 border-b border-gray-200 text-left text-sm font-semibold text-gray-700">
                  Type
                </th>
                <th className="py-3 px-4 border-b border-gray-200 text-left text-sm font-semibold text-gray-700">
                  Start Date
                </th>
                <th className="py-3 px-4 border-b border-gray-200 text-left text-sm font-semibold text-gray-700">
                  End Date
                </th>
                <th className="py-3 px-4 border-b border-gray-200 text-left text-sm font-semibold text-gray-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredServiceFees.length > 0 ? (
                filteredServiceFees.map((fee) => (
                  <tr key={fee.fee_id} className="hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4 border-b border-gray-200 whitespace-nowrap">
                      {fee.fee_name}
                    </td>
                    <td className="py-3 px-4 border-b border-gray-200">
                      {fee.fee_type === 'F'
                        ? `₹${parseFloat(fee.fee_amount).toFixed(2)}`
                        : `${parseFloat(fee.fee_amount).toFixed(2)}%`}
                    </td>
                    <td className="py-3 px-4 border-b border-gray-200">
                      {fee.fee_type === 'F' ? 'Fixed' : 'Percentage'}
                    </td>
                    <td className="py-3 px-4 border-b border-gray-200">
                      {new Date(fee.start_date).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4 border-b border-gray-200">
                      {new Date(fee.end_date).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4 border-b border-gray-200">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleFeeEditClick(fee)}
                          className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50 transition-colors"
                          title="Edit"
                        >
                          <FiEdit2 size={18} />
                        </button>
                        <button
                          onClick={() => handleFeeDelete(fee.fee_id)}
                          className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50 transition-colors"
                          title="Delete"
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="py-4 text-center text-gray-500">
                    {feeSearchTerm ? 'No matching service fees found.' : 'No service fees found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderTaxTab = () => {
    if (taxLoading) {
      return <div className="text-center py-4">Loading taxes...</div>;
    }

    if (taxError) {
      return <div className="text-center text-red-500 py-4">{taxError}</div>;
    }

    return (
      <div>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-4">
          <div className="relative w-full md:w-64">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiSearch className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search taxes..."
              value={taxSearchTerm}
              onChange={handleTaxSearchChange}
              className="pl-10 w-full p-2 border border-gray-300 rounded"
            />
          </div>
          <button
            onClick={() => {
              setIsEditingTax(false);
              setCurrentTax(null);
              setShowTaxModal(true);
              setTaxFormData({
                taxname: '',
                value: '',
                priority: '',
                definition: '',
                value_type: '',
              });
            }}
            className="flex items-center px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors"
          >
            <FiPlus className="mr-2" />
            Add New Tax
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead className="bg-gray-100">
              <tr>
                <th className="py-3 px-4 border-b border-gray-200 text-left text-sm font-semibold text-gray-700">
                  Name
                </th>
                <th className="py-3 px-4 border-b border-gray-200 text-left text-sm font-semibold text-gray-700">
                  Value
                </th>
                <th className="py-3 px-4 border-b border-gray-200 text-left text-sm font-semibold text-gray-700">
                  Type
                </th>
                <th className="py-3 px-4 border-b border-gray-200 text-left text-sm font-semibold text-gray-700">
                  Priority
                </th>
                <th className="py-3 px-4 border-b border-gray-200 text-left text-sm font-semibold text-gray-700">
                  Description
                </th>
                <th className="py-3 px-4 border-b border-gray-200 text-left text-sm font-semibold text-gray-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredTaxes.length > 0 ? (
                filteredTaxes.map((tax) => (
                  <tr key={tax.taxid} className="hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4 border-b border-gray-200 whitespace-nowrap">
                      {tax.taxname}
                    </td>
                    <td className="py-3 px-4 border-b border-gray-200">
                      {tax.value}
                      {tax.value_type === 'P' ? '%' : ''}
                    </td>
                    <td className="py-3 px-4 border-b border-gray-200">
                      {tax.value_type === 'P'
                        ? 'Percentage'
                        : tax.value_type === 'S'
                          ? 'Fixed'
                          : '-'}
                    </td>
                    <td className="py-3 px-4 border-b border-gray-200">{tax.priority}</td>
                    <td className="py-3 px-4 border-b border-gray-200 text-sm">
                      {tax.definition || '-'}
                    </td>
                    <td className="py-3 px-4 border-b border-gray-200">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleTaxEditClick(tax)}
                          className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50 transition-colors"
                          title="Edit"
                        >
                          <FiEdit2 size={18} />
                        </button>
                        <button
                          onClick={() => handleTaxDelete(tax.taxid)}
                          className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50 transition-colors"
                          title="Delete"
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="py-4 text-center text-gray-500">
                    {taxSearchTerm
                      ? 'No matching taxes found.'
                      : 'No taxes found for this property.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ==================== MAIN RENDER ====================

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <h2 className="text-xl font-bold mb-6">Tax & Service Fee Management</h2>

      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('tax')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'tax'
                ? 'border-black text-black'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Tax Management
          </button>
          <button
            onClick={() => setActiveTab('service-fee')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'service-fee'
                ? 'border-black text-black'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Service Fee Management
          </button>
        </nav>
      </div>

      {activeTab === 'tax' ? renderTaxTab() : renderServiceFeeTab()}

      <ServiceFeeModal />
      <TaxModal />

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default TaxManagement;
